import { signal } from "@preact/signals";
import type { ComponentChildren, RefObject } from "preact";
import { createPortal } from "preact/compat";
import { useCallback, useEffect, useId, useLayoutEffect, useRef } from "preact/hooks";

const MARGIN = 8;
const GAP = 6;

type Size = { width: number; height: number };

// Below the trigger and centred on it, flipped above when the bottom would not fit, and held inside the viewport.
export function placePopover(
	trigger: { left: number; top: number; width: number; height: number },
	panel: Size,
	viewport: Size,
): { left: number; top: number } {
	const centred = trigger.left + trigger.width / 2 - panel.width / 2;
	const left = Math.max(MARGIN, Math.min(centred, viewport.width - panel.width - MARGIN));
	const below = trigger.top + trigger.height + GAP;
	const fits = below + panel.height + MARGIN <= viewport.height;
	const top = fits ? below : Math.max(MARGIN, trigger.top - GAP - panel.height);
	return { left, top };
}

// One popover is open at a time, whichever view owns it.
const current = signal<string | null>(null);

export type PopoverState<T extends HTMLElement = HTMLElement> = {
	id: string;
	open: boolean;
	toggle: () => void;
	close: () => void;
	trigger: RefObject<T>;
	panel: RefObject<HTMLDivElement>;
};

// Open state, and its dismissal: a press outside, Escape (focus goes back to the trigger) or a scroll of anything the trigger sits in.
export function usePopover<T extends HTMLElement = HTMLElement>(): PopoverState<T> {
	const id = useId();
	const trigger = useRef<T>(null);
	const panel = useRef<HTMLDivElement>(null);
	const open = current.value === id;
	const close = () => {
		if (current.peek() === id) current.value = null;
	};

	useEffect(() => {
		if (!open) return undefined;
		const outside = (event: Event) => {
			const target = event.target as Node;
			if (!panel.current?.contains(target) && !trigger.current?.contains(target)) close();
		};
		const onEscape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			// Heard in the capture phase and consumed: the innermost layer takes the key, so neither the panel nor the board behind it also acts on it.
			event.preventDefault();
			event.stopPropagation();
			close();
			trigger.current?.focus();
		};
		// The trigger moves with a scroll of the page or of any container around it; other scrolls do not touch it.
		const scroll = (event: Event) => {
			if (!(event.target instanceof Node && event.target.contains(trigger.current))) return;
			const hadFocus = panel.current?.contains(document.activeElement);
			close();
			if (hadFocus) trigger.current?.focus({ preventScroll: true });
		};
		document.addEventListener("pointerdown", outside, true);
		document.addEventListener("keydown", onEscape, true);
		window.addEventListener("scroll", scroll, true);
		return () => {
			document.removeEventListener("pointerdown", outside, true);
			document.removeEventListener("keydown", onEscape, true);
			window.removeEventListener("scroll", scroll, true);
		};
	}, [open, id]);

	useEffect(() => close, [id]);

	const toggle = () => {
		current.value = open ? null : id;
	};
	return { id, open, close, toggle, trigger, panel };
}

// A portal into body, because a fixed element inside the sheet's container is positioned against the container.
export function PopoverPanel({
	popover,
	label,
	labelledBy,
	describedBy,
	class: className = "",
	children,
}: {
	popover: PopoverState<HTMLElement>;
	// The accessible name: a string, or the id of the heading inside.
	label?: string;
	labelledBy?: string;
	describedBy?: string;
	// Width and padding.
	class?: string;
	children: ComponentChildren;
}) {
	const place = useCallback(() => {
		const trigger = popover.trigger.current;
		const panel = popover.panel.current;
		if (!trigger || !panel) return;
		const { left, top } = placePopover(
			trigger.getBoundingClientRect(),
			{ width: panel.offsetWidth, height: panel.offsetHeight },
			{
				width: document.documentElement.clientWidth,
				height: window.innerHeight,
			},
		);
		panel.style.left = `${left}px`;
		panel.style.top = `${top}px`;
	}, [popover.trigger, popover.panel]);

	useLayoutEffect(place);

	// The viewport or the panel's own size changes without a render.
	useEffect(() => {
		const observer = new ResizeObserver(place);
		if (popover.panel.current) observer.observe(popover.panel.current);
		window.addEventListener("resize", place);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", place);
		};
	}, [place, popover.panel]);
	return createPortal(
		<div
			ref={popover.panel}
			id={popover.id}
			role="dialog"
			tabIndex={-1}
			aria-label={label}
			aria-labelledby={labelledBy}
			aria-describedby={describedBy}
			class={`fixed z-50 max-h-[calc(100vh-1rem)] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-line-strong bg-surface-raised text-ink shadow-md ${className}`}
		>
			{children}
		</div>,
		document.body,
	);
}
